import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCases } from '../../configs/useCases';

import VideoEmbed from '../../components/UseCaseComponents/VideoEmbed';
import HeroImagePreview from '../../components/UseCaseComponents/HeroImagePreview';
import HeroSwoopBackground from '../../components/UseCaseComponents/HeroSwoopBackground';
import ChatStarterLayout from '../../components/UseCases/ChatStarterLayout';
import VideoStarterLayout from '../../components/UseCases/VideoStarterLayout';
import DashboardStarterLayout from '../../components/UseCases/DashboardStarterLayout';
import MixedContentLayout from '../../components/UseCases/MixedContentLayout';
import SiteStarterLayout from '../../components/UseCases/SiteStarterLayout';
import VideoReportStarterLayout from '../../components/UseCases/VideoReportStarterLayout';
import VideoChatStarterLayout from '../../components/UseCases/VideoChatStarterLayout';
import SiteFooter from '../../components/SiteFooter/SiteFooter';
import ContactCTA from '../../components/UseCaseComponents/ContactCTA';

const layouts = {
  ChatStarterLayout,
  VideoStarterLayout,
  DashboardStarterLayout,
  MixedContentLayout,
  SiteStarterLayout,
  VideoReportStarterLayout,
  VideoChatStarterLayout,
  // QuickAppStarterLayout was wired in here for the "Agentic Readiness
  // Roadmap Tracker (Live Embed)" experiment -- removed along with that
  // use case (see useCases.js) since AWS's embedding API doesn't support
  // Quick Apps. The component file itself is left on disk unused, in case
  // it's ever needed again if AWS adds real support.
};

const UseCaseView = () => {
  const { useCaseId } = useParams();

  const useCase = useCases.find(
    (entry) => entry.id === useCaseId
  );

  // Unlisted (link-only) use cases get a noindex tag so search engines
  // don't surface them; removed again when navigating away.
  useEffect(() => {
    if (!useCase?.unlisted) return undefined;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [useCase]);

  if (!useCase) {
    // A bad/typo'd URL (e.g. a truncated or mistyped use case slug) still
    // matches the catch-all "/:useCaseId" route in App.jsx and lands here
    // with no matching entry in useCases.js. Styled as a friendly not-found
    // message -- no "404" label, sits near the top of the page like every
    // other view instead of centered mid-page -- with an on-brand primary
    // button matching ContactCTA's button below, instead of the old bare
    // heading + underlined text link.
    return (
      <div className="flex flex-1 flex-col items-center px-4 py-6 text-center sm:px-6 sm:py-8 lg:px-10 lg:py-10">
        <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
          Oops, this page doesn&rsquo;t exist
        </h1>

        <p className="mt-4 max-w-md text-gray-600">
          The page you&rsquo;re looking for may have been moved or the link
          might be off. Let&rsquo;s get you back on track.
        </p>

        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          <span aria-hidden="true">&larr;</span>
          Back to home
        </Link>
      </div>
    );
  }

  const Layout = layouts[useCase.layout];

  // Opt-in gradient hero: a use case can set `heroBanner: true` (see
  // useCases.js) to get its title/description shown on a full-bleed
  // colorful gradient band with its video sitting alongside it, instead of
  // the plain white header everything else uses. When this is on, the
  // layout component itself should NOT also render that same video up top
  // (see VideoReportStarterLayout/DynamicPricingOnePager).
  // heroBanner no longer requires a real video.url -- a use case that
  // doesn't have a recorded walkthrough yet (see useCases.js) still gets
  // the gradient hero band with the same two-column, fixed-height layout
  // as every other use case; the video column just renders empty (see
  // heroVideo?.url below) until a real video exists, at which point
  // adding `video: { url }` in useCases.js fills it in automatically.
  const heroVideo = useCase.components?.video;
  // Static screenshot/mockup stand-in for a use case that has a preview
  // image but no recorded walkthrough yet (see HeroImagePreview) -- takes
  // over the same video column heroVideo would otherwise fill.
  const heroImage = useCase.components?.heroImage;
  const showHeroBanner = Boolean(useCase.heroBanner);

  return (
    <div className="flex flex-1 flex-col">
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
      {!showHeroBanner && (
        <Link
          to="/"
          className="mb-6 flex w-fit items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
        >
          <span aria-hidden="true">&larr;</span>
          Home
        </Link>
      )}

      {showHeroBanner ? (
        // Full-bleed: negative margins cancel this container's own
        // horizontal padding so the gradient reaches edge-to-edge, then
        // re-adds its own (larger) padding inside.
        <div className="relative isolate -mx-4 overflow-hidden bg-[#e7e6e1] px-4 pb-10 pt-4 sm:-mx-6 sm:px-8 sm:pb-14 sm:pt-6 lg:-mx-10 lg:px-14">
          {/* mobileAlign="xMax": on mobile this box goes single-column
              (title/description stacked above the video) and gets much
              taller relative to its width than on desktop, which zooms
              the cover-cropped swoosh in on a narrow vertical strip of
              its canvas. The default "xMid" crop lands that strip on the
              flat gray left of the ribbons -- see HeroSwoopBackground's
              own comment -- so mobile needs "xMax" instead to keep the
              crop over the actual colorful ribbons. Desktop keeps the
              default "xMid" (unset here), which already shows both
              ribbons fine on that wider, shorter box. No mobileBlurPx now
              that the title/description sit in their own solid card (see
              below) instead of directly on the graphic -- the card
              handles legibility on its own, so the swoosh underneath can
              stay sharp/unblurred. */}
          <HeroSwoopBackground mobileAlign="xMax" />

          {/* Back link now lives inside the gradient hero box itself
              (top-left corner), instead of floating above it on the
              plain white page background. */}
          <Link
            to="/"
            className="relative mb-6 flex w-fit items-center gap-2 rounded-md border border-black/20 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm backdrop-blur-sm hover:bg-white"
          >
            <span aria-hidden="true">&larr;</span>
            Home
          </Link>

          {/* min-h keeps the gradient box's height stable regardless of
              how big/small the video ends up -- without it, shrinking the
              video shrinks the whole row (since the row's height just
              follows its tallest child), and the background collapses
              along with it. */}
          <div className="relative grid grid-cols-1 items-center gap-10 lg:min-h-[26rem] lg:grid-cols-2">
            {/* Below sm, this now sits in a mostly-solid white card on top
                of the blurred swoosh instead of relying on white text +
                shadow/scrim tricks directly on the graphic. Earlier
                attempts: a fully translucent card (jarring hard edge
                against a sharp, unblurred swoosh), then a scrim + text-
                shadow (readable, but the swoosh itself still showed
                through the letters unevenly). A near-opaque card (95%
                white) reads as a clean, deliberate card the way the West
                Loop Strategy marketing site's own mobile hero does, and
                still lets a sliver of the blurred color show through at
                the edges instead of a flat white box. Resets to fully
                transparent/no padding at sm and up, where the plain
                desktop crop already reads fine with plain black text and
                no card at all. */}
            <div className="rounded-xl border border-black/10 bg-white/90 p-5 shadow-lg sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
              <h1
                className="text-3xl font-bold text-gray-900 sm:text-4xl sm:text-black lg:text-5xl"
                style={{ textWrap: 'balance' }}
              >
                {useCase.title}
              </h1>

              <p className="mt-4 max-w-xl text-gray-600 sm:text-black">
                {useCase.description || 'Coming soon.'}
              </p>
            </div>

            {heroVideo?.url && (
              <VideoEmbed
                url={heroVideo.url}
                title={useCase.title}
                // Shrunk to 80% width and right-aligned within its column.
                // The grid's own lg:min-h keeps the row (and the gradient
                // background) a stable height, so items-center centers this
                // against that fixed height rather than against whatever
                // height the video happens to be -- no manual margin needed.
                className="w-full lg:ml-auto lg:w-4/5"
                aspectClassName="aspect-[16/7.8]"
                chrome
                // No hardcoded badge here anymore -- "3 min demo" was showing
                // on every use case's video regardless of that video's real
                // length. VideoEmbed now reads the actual duration off the
                // file itself and generates this badge from that.
                caption="See the workflow in action"
              />
            )}

            {!heroVideo?.url && heroImage?.src && (
              <HeroImagePreview
                src={heroImage.src}
                mobileSrc={heroImage.mobileSrc}
                mobileAspectClassName={heroImage.mobileAspectClassName}
                alt={heroImage.alt || `${useCase.title} preview`}
                caption={heroImage.caption}
                // Wider than the lg:w-4/5 real videos use (see heroVideo
                // above) -- a static screenshot reads better bigger, and
                // unlike the video treatment there's no separate "chrome"
                // framing competing for space here. Below sm, HeroImagePreview
                // swaps to heroImage.mobileSrc (a dedicated taller/narrower
                // render, see useCases.js) when one is set, since the wide
                // 16/7.8 desktop crop reads too small on a phone.
                className="w-full lg:ml-auto lg:w-full"
                aspectClassName="aspect-[16/7.8]"
              />
              // No recorded walkthrough for this use case yet -- a static
              // preview image fills the same video column. Once a real
              // video exists, add `video: { url }` in useCases.js and it
              // takes priority over heroImage automatically.
            )}
          </div>
        </div>
      ) : (
        <>
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {useCase.title}
          </h1>

          <p className="mt-4 max-w-2xl text-gray-600">
            {useCase.description || 'Coming soon.'}
          </p>
        </>
      )}

      {Layout ? (
        <Layout useCase={useCase} />
      ) : (
        <p className="mt-6 text-gray-500">
          This use case layout is not configured yet.
        </p>
      )}
    </div>
    <ContactCTA />
    <SiteFooter />
    </div>
  );
};

export default UseCaseView;
